import { Plus } from "lucide-mui"
import { DateTime } from "luxon"
import React, { useCallback, useEffect, useMemo, useRef } from "react"

import i18n from "@/i18n"

import { TIMELINE_COLORS } from "./styles"

export type TimelineBusyBlock = { startTime: string; endTime: string }
export type TimelineWindow = { start: DateTime; end: DateTime }

type MobileAvailabilityTimelineProps = {
  /** All opening-hours slots (`[label, DateTime]`) — defines the strip's full-day range. */
  timeOptions: [string, DateTime][]
  /**
   * Exact-minute opening bounds for the date; when provided they define the strip range (so non-:00
   * venues don't show a pre-open tick). Falls back to the `timeOptions` endpoints when omitted.
   */
  openingRange?: { opensAt: DateTime; closesAt: DateTime } | null
  /** The selected table's other reservations, drawn as grey "Booked" blocks. */
  busyBlocks: TimelineBusyBlock[]
  closedUntil?: DateTime | null
  /** Confirmed guest window (time + table set) → blue block, red where it hits a busy block. */
  confirmedWindow?: TimelineWindow | null
  /** Proposed window (table set, time not yet confirmed) → green block with a `+` accept handle. */
  proposedWindow?: TimelineWindow | null
  onConflictChange?: (hasConflicts: boolean) => void
  onAcceptProposed?: () => void
}

type GuestDescriptor = {
  window: TimelineWindow
  isProposed: boolean
  state: "confirmed" | "proposed" | "conflict"
  fill: string
  border: string
}

const HOUR_WIDTH_PX = 70
const PX_PER_MINUTE = HOUR_WIDTH_PX / 60
const TRACK_HEIGHT = 20
const BLOCK_HEIGHT = 32
const HOUR_TICK_HEIGHT = 24
const MIN_BLOCK_WIDTH = 3
const SIDE_PADDING = 20

const formatWindowLabel = (start: DateTime, end: DateTime): string =>
  `${start.toFormat("HH:mm")} - ${end.toFormat("HH:mm")}`

const intersect = (a: TimelineWindow, b: TimelineWindow): TimelineWindow | null => {
  const start = a.start > b.start ? a.start : b.start
  const end = a.end < b.end ? a.end : b.end
  return end > start ? { start, end } : null
}

/** The single guest block to draw (blue / green / red), or null when the table has no window. */
const buildGuestDescriptor = (
  confirmedWindow: TimelineWindow | null | undefined,
  proposedWindow: TimelineWindow | null | undefined,
  hasConflict: boolean,
): GuestDescriptor | null => {
  if (confirmedWindow) {
    return {
      window: confirmedWindow,
      isProposed: false,
      state: hasConflict ? "conflict" : "confirmed",
      fill: TIMELINE_COLORS.confirmedFill,
      border: TIMELINE_COLORS.confirmedBorder,
    }
  }
  if (proposedWindow) {
    return {
      window: proposedWindow,
      isProposed: true,
      state: "proposed",
      fill: TIMELINE_COLORS.proposedFill,
      border: TIMELINE_COLORS.proposedBorder,
    }
  }
  return null
}

/**
 * Display-only availability strip for the selected table (Figma 1283-6812 / 1283-7658). Blocks are
 * absolutely positioned over a solid track by a fixed px-per-minute scale — there is no drag; time is
 * edited via the dropdown pill or accepted via the green `+` handle. Three semantic states:
 *  - blue confirmed window (time + table set)
 *  - green proposed window with a `+` accept handle (table set, time cleared)
 *  - red overlap drawn on top of a conflicting busy block / the confirmed block
 */
const MobileAvailabilityTimeline: React.FC<MobileAvailabilityTimelineProps> = ({
  timeOptions,
  openingRange,
  busyBlocks,
  closedUntil,
  confirmedWindow,
  proposedWindow,
  onConflictChange,
  onAcceptProposed,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  const range = useMemo(() => {
    if (openingRange) {
      const { opensAt: start, closesAt: end } = openingRange
      return { start, end, totalMinutes: end.diff(start, "minutes").minutes }
    }
    if (timeOptions.length === 0) return null
    const start = timeOptions[0][1]
    const end = timeOptions[timeOptions.length - 1][1].plus({ minutes: 15 })
    return { start, end, totalMinutes: end.diff(start, "minutes").minutes }
  }, [openingRange, timeOptions])

  const pxAt = useCallback(
    (dt: DateTime): number => {
      if (!range) return 0
      const minutes = dt.diff(range.start, "minutes").minutes
      return Math.min(Math.max(minutes, 0), range.totalMinutes) * PX_PER_MINUTE
    },
    [range],
  )

  const contentWidth = range ? range.totalMinutes * PX_PER_MINUTE : 0

  const hourTicks = useMemo(() => {
    if (!range) return []
    const ticks: { x: number; label: string }[] = []
    let cursor = range.start.startOf("hour")
    if (cursor < range.start) cursor = cursor.plus({ hours: 1 })
    while (cursor <= range.end) {
      ticks.push({ x: pxAt(cursor), label: cursor.toFormat("HH:mm") })
      cursor = cursor.plus({ hours: 1 })
    }
    return ticks
  }, [range, pxAt])

  const bookedBlocks = useMemo(() => {
    if (!range) return []
    return busyBlocks
      .map((block) => ({ start: DateTime.fromISO(block.startTime), end: DateTime.fromISO(block.endTime) }))
      .filter((block) => block.start.isValid && block.end.isValid && block.end > range.start && block.start < range.end)
      .map((block) => {
        const left = pxAt(block.start)
        return { left, width: Math.max(pxAt(block.end) - left, MIN_BLOCK_WIDTH), interval: block }
      })
  }, [busyBlocks, range, pxAt])

  // Busy blocks that the confirmed window collides with: the whole block reddens at track height,
  // and the [window ∩ block] overlap reddens at full block height (drawn on top of the guest block).
  const conflictingBooked = useMemo(
    () => (confirmedWindow ? bookedBlocks.filter((block) => intersect(confirmedWindow, block.interval)) : []),
    [confirmedWindow, bookedBlocks],
  )

  const conflictIntervals = useMemo(
    () =>
      conflictingBooked
        .map((block) => (confirmedWindow ? intersect(confirmedWindow, block.interval) : null))
        .filter((overlap): overlap is TimelineWindow => overlap !== null),
    [confirmedWindow, conflictingBooked],
  )

  const startsBeforeOpen = Boolean(confirmedWindow && closedUntil && confirmedWindow.start < closedUntil)
  const hasConflict = conflictIntervals.length > 0 || startsBeforeOpen

  useEffect(() => {
    onConflictChange?.(hasConflict)
  }, [hasConflict, onConflictChange])

  // Reset the shared conflict signal when the strip unmounts (e.g. the table is cleared): otherwise
  // the surviving time pill would stay red in the time-only state, since nothing else resets it.
  useEffect(() => {
    return () => onConflictChange?.(false)
  }, [onConflictChange])

  const guest = buildGuestDescriptor(confirmedWindow, proposedWindow, hasConflict)
  const guestWindow = guest?.window ?? null

  useEffect(() => {
    const element = scrollRef.current
    if (!element || !guestWindow || contentWidth === 0) return
    const left = pxAt(guestWindow.start)
    const center = SIDE_PADDING + left + (pxAt(guestWindow.end) - left) / 2
    const target = Math.max(0, center - element.clientWidth / 2)
    if (typeof element.scrollTo === "function") {
      element.scrollTo({ left: target, behavior: "smooth" })
    } else {
      element.scrollLeft = target
    }
  }, [guestWindow, pxAt, contentWidth])

  if (!range) return null

  const guestLeft = guestWindow ? pxAt(guestWindow.start) : 0
  const guestWidth = guestWindow ? Math.max(pxAt(guestWindow.end) - guestLeft, MIN_BLOCK_WIDTH) : 0

  return (
    <div ref={scrollRef} className="overflow-x-auto" data-testid="public-reservation-modal-mobile-timeline-strip">
      <div className="px-5 pb-2 pt-3">
        <div className="relative" style={{ width: contentWidth }}>
          <div className="relative" style={{ height: BLOCK_HEIGHT }}>
            <div
              className="absolute inset-x-0 bottom-0 rounded"
              style={{ height: TRACK_HEIGHT, backgroundColor: TIMELINE_COLORS.track }}
            />

            {hourTicks.map((tick) => (
              <div
                key={`tick-${tick.label}`}
                className="absolute bottom-0"
                style={{ left: tick.x, width: 1, height: HOUR_TICK_HEIGHT, backgroundColor: TIMELINE_COLORS.divider }}
              />
            ))}

            {bookedBlocks.map((block, index) => (
              <div
                key={`booked-${index}`}
                className="absolute bottom-0 rounded"
                aria-label={i18n.t("app.public.venue.table_selection.timeline_booked")}
                data-testid="public-reservation-modal-mobile-timeline-booked"
                style={{
                  left: block.left,
                  width: block.width,
                  height: TRACK_HEIGHT,
                  backgroundColor: TIMELINE_COLORS.bookedFill,
                  border: `1px solid ${TIMELINE_COLORS.bookedBorder}`,
                }}
              />
            ))}

            {guest ? (
              <div
                className="absolute bottom-0 flex items-center justify-center rounded-md"
                data-testid="public-reservation-modal-mobile-timeline-block"
                data-state={guest.state}
                style={{
                  left: guestLeft,
                  width: guestWidth,
                  height: BLOCK_HEIGHT,
                  backgroundColor: guest.fill,
                  border: `1px solid ${guest.border}`,
                }}
              >
                <span className="truncate px-2 text-[12px] font-medium leading-none text-white">
                  {formatWindowLabel(guest.window.start, guest.window.end)}
                </span>
                {guest.isProposed ? (
                  <button
                    type="button"
                    onClick={onAcceptProposed}
                    aria-label={i18n.t("app.public.venue.table_selection.timeline_accept_proposed")}
                    data-testid="public-reservation-modal-mobile-timeline-accept"
                    className="absolute -right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white"
                    style={{ backgroundColor: TIMELINE_COLORS.proposedHandle }}
                  >
                    <Plus className="h-4 w-4 text-white" />
                  </button>
                ) : null}
              </div>
            ) : null}

            {conflictingBooked.map((block, index) => (
              <div
                key={`conflict-booked-${index}`}
                className="absolute bottom-0 rounded"
                data-testid="public-reservation-modal-mobile-timeline-conflict-booked"
                style={{
                  left: block.left,
                  width: block.width,
                  height: TRACK_HEIGHT,
                  backgroundColor: TIMELINE_COLORS.conflictFill,
                  border: `1px solid ${TIMELINE_COLORS.conflictBorder}`,
                }}
              />
            ))}

            {conflictIntervals.map((overlap, index) => {
              const left = pxAt(overlap.start)
              return (
                <div
                  key={`conflict-${index}`}
                  className="absolute bottom-0 rounded-md"
                  data-testid="public-reservation-modal-mobile-timeline-conflict"
                  style={{
                    left,
                    width: Math.max(pxAt(overlap.end) - left, MIN_BLOCK_WIDTH),
                    height: BLOCK_HEIGHT,
                    backgroundColor: TIMELINE_COLORS.conflictFill,
                    border: `1px solid ${TIMELINE_COLORS.conflictBorder}`,
                  }}
                />
              )
            })}
          </div>

          <div className="relative mt-2 h-3">
            {hourTicks.map((tick) => (
              <span
                key={`label-${tick.label}`}
                className="absolute -translate-x-1/2 whitespace-nowrap text-[10px] leading-none"
                style={{ left: tick.x, color: TIMELINE_COLORS.axisLabel }}
              >
                {tick.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MobileAvailabilityTimeline
